// @soft-depth
import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Handshake, Lightbulb, Route } from 'lucide-react';
import DiscChemistryCard from './DiscChemistryCard';
import PlaybookCard from './PlaybookCard';
import { ObjectionEditButton, ObjectionList } from './ObjectionCard';
import type { ObjectionFacts } from './clientObjections';
import { IdeaCard, IdeaMeetingFlow, IdeaPoints } from './IdeaKit';
import { SELL_POINT_ORDER, discOrderedPoints } from './discPoints';
import { discLabel, isDiscLetter } from '../../../constants/disc';
import { useCoverQuiz } from '../../../views/CoverQuiz/coverQuizStorage';
import { coverQuizSignals } from '../../../views/CoverQuiz/coverQuizData';
import { quizLangOf } from '../../../views/CoverQuiz/quizLang';

/**
 * Smart IDEA's 溝通方式 tab, laid out like 招募 IDEA's so the adviser works the
 * same way whether recruiting or selling: 我的溝通風格 → DISC 對話建議 →
 * 會談流程 → 可以分享的亮點. The meeting flow is the business one — 了解需要
 * reads the client's 保障小測驗, 化解異議 carries what used to be the 異議處理
 * card.
 */

type IntlLike = {
  formatMessage: (descriptor: { id: string }, values?: Record<string, unknown>) => string;
  locale?: string;
};

// 可以分享的亮點 for a client: what the adviser keeps doing after the sale.
const SELL_POINTS = [
  { key: 'sos', icon: '🆘' },
  { key: 'review', icon: '🔍' },
  { key: 'claims', icon: '🤝' },
  { key: 'portfolio', icon: '📱' }
] as const;

const Footnote = (props: { children: React.ReactNode }) => (
  <Typography component="p" variant="body2" sx={{ m: 0, color: 'text.secondary' }}>
    {props.children}
  </Typography>
);

/** 了解需要 — where the 保障小測驗 says to start: gaps, interests, life topics, cautions. */
const NeedsStep = (props: { clientId?: string | null; intl: IntlLike; onGoToOverview?: () => void }) => {
  const { clientId, intl, onGoToOverview } = props;
  const t = (id: string) => intl.formatMessage({ id });
  const quiz = useCoverQuiz(clientId);

  if (!quiz) {
    return (
      <Stack
        direction="row"
        data-testid="smart-idea-need-empty"
        sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1.25 }}
      >
        <Footnote>{t('smart-idea-flow-need-empty')}</Footnote>
        {onGoToOverview && (
          <Button size="small" variant="contained" onClick={onGoToOverview}>
            {t('smart-idea-flow-need-send')}
          </Button>
        )}
      </Stack>
    );
  }

  const lang = quizLangOf(intl.locale);
  const signals = coverQuizSignals(quiz.answers, lang);
  const talk = signals.knowledge.wrong.map(q => q.talkingPoint[lang]);
  const interests = signals.interests.map(o => `${o.icon} ${o.label[lang]}`);
  const colon = lang === 'en' ? ': ' : '：';
  const service = signals.service.map(s => `${s.short[lang]}${colon}${s.option.icon} ${s.option.label[lang]}`);
  const blocks = [
    { key: 'service', label: t('smart-idea-flow-need-service'), items: service, list: false },
    { key: 'talk', label: t('smart-idea-flow-need-talk'), items: talk, list: true },
    { key: 'interests', label: t('smart-idea-flow-need-interests'), items: interests, list: false },
    { key: 'topics', label: t('smart-idea-flow-need-topics'), items: signals.topics, list: false },
    { key: 'cautions', label: t('smart-idea-flow-need-cautions'), items: signals.cautions, list: true }
  ].filter(block => block.items.length > 0);

  if (!blocks.length) {
    return <Footnote>{t('smart-idea-flow-need-none')}</Footnote>;
  }
  return (
    <Stack data-testid="smart-idea-need" sx={{ gap: 1.25 }}>
      {blocks.map(block => (
        <div key={block.key} data-block={block.key}>
          <Typography
            component="span"
            variant="subtitle2"
            sx={{ display: 'block', mb: 0.5, fontWeight: 700, color: 'text.secondary' }}
          >
            {block.label}
          </Typography>
          {block.list ? (
            <Box component="ul" sx={{ m: 0, pl: 2.25, color: 'grey.800' }}>
              {block.items.map(item => (
                <Typography component="li" variant="body2" key={item}>
                  {item}
                </Typography>
              ))}
            </Box>
          ) : (
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
              {block.items.map(item => (
                <Chip
                  key={item}
                  label={item}
                  sx={{ height: 'auto', minHeight: 28, '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
                />
              ))}
            </Stack>
          )}
        </div>
      ))}
    </Stack>
  );
};

export interface SmartIdeaCommunicationProps {
  intl: IntlLike;
  clientId?: string | null;
  clientName: string;
  /** The letter on file, or the 保障小測驗's when there is none. */
  disc?: string | null;
  /** The letter on file only — DISC 對話建議 labels a quiz letter as such. */
  personality?: string | null;
  facts: ObjectionFacts;
  /** Takes the adviser to 客戶概覽, where the 保障小測驗 is sent. */
  onGoToOverview?: () => void;
  playbookRef?: React.Ref<HTMLElement>;
}

const SmartIdeaCommunication = (props: SmartIdeaCommunicationProps) => {
  const { intl, clientId, clientName, disc, personality, facts, onGoToOverview, playbookRef } = props;
  const t = (id: string, values?: Record<string, unknown>) => intl.formatMessage({ id }, values);
  const [editingObjections, setEditingObjections] = useState(false);

  return (
    <>
      <DiscChemistryCard mode="business" name={clientName} disc={disc} intl={intl} />
      <PlaybookCard
        clientId={clientId}
        clientName={clientName}
        personality={personality}
        intl={intl}
        cardRef={playbookRef}
      />

      <IdeaCard
        id="meeting-flow"
        testId="smart-idea-section"
        icon={<Route size={18} aria-hidden="true" />}
        title={t('clientv2-recruit-flow')}
        hint={t('clientv2-recruit-flow-hint')}
      >
        <IdeaMeetingFlow
          testId="smart-idea-flow"
          minutesLabel={n => t('clientv2-recruit-flow-minutes', { n })}
          steps={[
            {
              id: 'open',
              marker: 1,
              title: t('clientv2-recruit-flow-open'),
              minutes: 5,
              note: t('clientv2-recruit-flow-open-note')
            },
            {
              id: 'need',
              marker: 2,
              title: t('smart-idea-flow-need'),
              minutes: 10,
              note: t('smart-idea-flow-need-hint'),
              content: <NeedsStep clientId={clientId} intl={intl} onGoToOverview={onGoToOverview} />
            },
            {
              id: 'objection',
              marker: 3,
              title: t('smart-idea-flow-objection'),
              minutes: 10,
              action: clientId ? (
                <ObjectionEditButton
                  editing={editingObjections}
                  onToggle={() => setEditingObjections(!editingObjections)}
                  intl={intl}
                />
              ) : undefined,
              note: t('smart-idea-flow-objection-hint'),
              content: (
                <ObjectionList clientId={clientId} facts={facts} intl={intl} editing={editingObjections} disc={disc} />
              )
            },
            {
              id: 'close',
              marker: <Handshake size={14} />,
              title: t('clientv2-recruit-flow-close'),
              minutes: 5,
              // How to close depends on who is across the table.
              note: t(isDiscLetter(disc) ? `smart-idea-flow-close-line-${disc}` : 'smart-idea-flow-close-line'),
              close: true
            }
          ]}
        />
      </IdeaCard>

      <IdeaCard
        id="talking-points"
        testId="smart-idea-section"
        icon={<Lightbulb size={18} aria-hidden="true" />}
        title={t('clientv2-recruit-talking-points')}
      >
        <IdeaPoints
          testId="smart-idea-talking-points"
          hint={
            isDiscLetter(disc)
              ? t('clientv2-idea-points-disc-hint', { type: discLabel(disc, intl.locale) })
              : t('clientv2-recruit-talking-points-hint')
          }
          leadLabel={t('clientv2-idea-points-lead')}
          points={discOrderedPoints(SELL_POINTS, SELL_POINT_ORDER, disc, key => `smart-idea-point-${key}-desc`).map(
            point => ({
              key: point.key,
              icon: point.icon,
              title: t(`smart-idea-point-${point.key}`),
              desc: t(point.descId),
              lead: point.lead
            })
          )}
        />
      </IdeaCard>
    </>
  );
};

export default SmartIdeaCommunication;
